import React from 'react';
import { DEFAULT_AVATAR } from '../../../../../shared/utils/matrix/matrixRoomUtils';
import { formatMissedCallNoticeLabel } from '../../../../../shared/utils/chatUtil';
import ChatMessageStatus from './ChatMessageStatus';

/**
 * Missed call notice bubble with optional call-back action.
 * @param {Object} props
 * @param {{ media: string }} props.missedCallInfo - Parsed missed-call payload (e.g. from parseMissedCallNotice)
 * @param {string|null} [props.roomId] - Chat room id for navigation
 * @param {boolean} [props.pending]
 * @param {boolean} [props.failed]
 * @param {string} props.timeLabel - Formatted time string
 * @param {boolean} props.isMine - True when the logged-in user initiated the unanswered call (outgoing).
 * @param {string} props.otherAvatar - Avatar URL for incoming (caller) side
 * @param {(kind: 'voice'|'video') => void} [props.onCallBack] - Opens call request flow (same as chat toolbar)
 */
const ChatMissedCall = ({
  missedCallInfo,
  roomId = null,
  pending = false,
  failed = false,
  timeLabel,
  isMine = false,
  otherAvatar,
  onCallBack,
}) => {
  const handleMissedCallBack = () => {
    if (!roomId || typeof onCallBack !== 'function') return;
    const kind = missedCallInfo?.media === 'video' ? 'video' : 'voice';
    onCallBack(kind);
  };

  const title = formatMissedCallNoticeLabel(missedCallInfo, isMine);
  const callActionLabel = isMine ? 'Call Again' : 'Call Back';
  const isVideo = missedCallInfo.media === 'video';

  return (
    <div
      className={`cm_chat cm_chat_missed_call ${isMine ? 'your_msg' : 'incomming_msg'}`}
    >
      {!isMine && (
        <img
          className="sender_img"
          src={otherAvatar || DEFAULT_AVATAR}
          alt=""
          aria-hidden
        />
      )}
      <div className="cm_chat_content">
        <div
          className="cm_missed_call_card"
          role="status"
          aria-label={title}
        >
          <div className="cm_missed_call_card_top">
            <div
              className={`cm_missed_call_icon_wrap${isVideo ? ' cm_missed_call_icon_wrap--video' : ' cm_missed_call_icon_wrap--voice'}`}
              aria-hidden
            >
              {isVideo ? (
                <i className="fa-solid fa-video" />
              ) : (
                <i className="fa-solid fa-phone-slash" />
              )}
            </div>
            <div className="cm_missed_call_card_text">
              <span className="cm_missed_call_title">{title}</span>
            </div>
          </div>
          <button
            type="button"
            className="cm_missed_call_callback"
            onClick={handleMissedCallBack}
            disabled={!roomId || typeof onCallBack !== 'function'}
          >
            {callActionLabel}
          </button>
        </div>
        <div className="cm_chat_meta cm_chat_meta_missed">
          <ChatMessageStatus pending={pending} failed={failed} />
          {timeLabel ? <time>{timeLabel}</time> : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChatMissedCall);
